/**
 * App — the auth gate. All session state lives in useAuth; the Console's tabs are
 * declared in components/ConsoleShell.jsx and fetch their own data.
 *
 * There is ONE shell. The Console has no RBAC and no view modes: it is a
 * single-audience customer surface (ADR 0004 / E06) whose only user type is the
 * paying customer, and the operator/research dual view this file used to select
 * between has been removed along with its shell, tab registry and four tabs. RBAC
 * belongs to Argus, which is a separate product with its own identity store (E07).
 */

import LoginScreen from "./components/LoginScreen";
import RegisterScreen from "./components/RegisterScreen";
import VerifyEmail from "./components/VerifyEmail";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import ConsoleShell from "./components/ConsoleShell";
import useAuth from "./hooks/useAuth";

export default function App() {
  const {
    loggedIn, authenticating, user, setUser, displayName, password, setPassword,
    loginLog, loginError, loginNotice, needsVerification, authView, setAuthView,
    verifyPath, verifyToken, doLogin, logout,
    handleRegistered, handleVerified, leaveVerify, resendVerification, openRegister,
    resendingVerification, resendCooldownRemaining,
    resetPath, resetToken, completeReset, leaveReset, requestNewResetLink,
    openForgot, closeForgot,
  } = useAuth();

  // L01 — the magic-link landing (routerless): /verify-email?token=<raw>. Takes
  // priority over the auth gate so the click both verifies AND auto-logs-in;
  // handleVerified then clears the path + enters the Console.
  if (verifyPath) {
    return (
      <VerifyEmail
        token={verifyToken}
        onVerified={handleVerified}
        onBackToLogin={leaveVerify}
      />
    );
  }

  // F14 — the emailed reset link (routerless): /reset-password?token=<raw>. Takes
  // priority over the auth gate for the same reason the verify link does: a
  // customer who cannot sign in is exactly who follows this link, so it must not
  // sit behind a login. Unlike verify it does NOT auto-login — completeReset
  // tears the session down instead (FR10).
  if (resetPath) {
    return (
      <ResetPassword
        token={resetToken}
        onResetComplete={completeReset}
        onBackToLogin={leaveReset}
        onRequestNewLink={requestNewResetLink}
      />
    );
  }

  if (!loggedIn) {
    return (
      <div>
        {authView === "register" ? (
          <RegisterScreen
            onRegistered={handleRegistered}
            onBackToLogin={() => setAuthView("login")}
          />
        ) : authView === "forgot" ? (
          <ForgotPassword onBackToLogin={closeForgot} />
        ) : (
          <LoginScreen
            user={user} setUser={setUser}
            password={password} setPassword={setPassword}
            loginLog={loginLog} onConnect={doLogin}
            onRegister={openRegister}
            error={loginError}
            authenticating={authenticating}
            needsVerification={needsVerification}
            onResendVerification={resendVerification}
            resendingVerification={resendingVerification}
            resendCooldown={resendCooldownRemaining}
            onForgotPassword={openForgot}
            notice={loginNotice}
          />
        )}
      </div>
    );
  }

  return <ConsoleShell user={user} displayName={displayName} onLogout={logout} />;
}
